import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@api";
import { useAuth } from "../../../../context/AuthContext.jsx";
import "./NavbarSettings.css";

const DEFAULT_NAVBAR_SETTINGS = {
  navbar_height: 100,
  navbar_logo_height: 84,
  navbar_home_mode: "solid",
  navbar_home_background_color: "#ffffff",
  navbar_home_link_color: "#ffffff",
  navbar_home_border_color: "transparent",
  navbar_inner_background_color: "#ffffff",
  navbar_inner_link_color: "#111111",
  navbar_inner_border_color: "transparent",

  footer_home_background_color: "#ffffff",
  footer_home_text_color: "#555555",
  footer_home_link_color: "#555555",
  footer_home_border_color: "transparent",

  footer_inner_background_color: "#ffffff",
  footer_inner_text_color: "#555555",
  footer_inner_link_color: "#555555",
  footer_inner_border_color: "transparent",

  footer_padding_y: 24,
};
function NavbarSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settingsTab, setSettingsTab] = useState("navbar");
  const [organization, setOrganization] = useState(null);
  const [navPages, setNavPages] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_NAVBAR_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [previewMode, setPreviewMode] = useState("homepage");
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    let alive = true;

    const loadNavbarSettings = async () => {
      try {
        const [organizationRes, settingsRes, navPagesRes] = await Promise.all([
          api.get(`/api/organizations/${id}`),
          api.get(`/api/organizations/${id}/navbar-settings`),
          api.get("/api/organizations/public/nav-pages", {
            params: {
              organizationId: id,
            },
          }),
        ]);

        if (!alive) return;

        const loadedOrganization =
          organizationRes.data?.organization || null;

        const settings =
          settingsRes.data?.settings ||
          settingsRes.data?.organizationSettings ||
          settingsRes.data ||
          {};

        setOrganization(loadedOrganization);
        setNavPages(navPagesRes.data?.pages || []);

        setFormData({
          navbar_height:
            Number(settings.navbar_height) ||
            DEFAULT_NAVBAR_SETTINGS.navbar_height,
          navbar_logo_height:
            Number(settings.navbar_logo_height) ||
            DEFAULT_NAVBAR_SETTINGS.navbar_logo_height,
          navbar_home_mode:
            settings.navbar_home_mode === "overlay"
              ? "overlay"
              : "solid",

          navbar_home_link_color:
            settings.navbar_home_link_color ||
            DEFAULT_NAVBAR_SETTINGS.navbar_home_link_color,

          navbar_inner_background_color:
            settings.navbar_inner_background_color ||
            DEFAULT_NAVBAR_SETTINGS.navbar_inner_background_color,

          navbar_inner_link_color:
            settings.navbar_inner_link_color ||
            DEFAULT_NAVBAR_SETTINGS.navbar_inner_link_color,

          navbar_home_background_color:
            settings.navbar_home_background_color ||
            DEFAULT_NAVBAR_SETTINGS.navbar_home_background_color,

          navbar_home_border_color:
            settings.navbar_home_border_color ||
            DEFAULT_NAVBAR_SETTINGS.navbar_home_border_color,

          navbar_inner_border_color:
            settings.navbar_inner_border_color ||
            DEFAULT_NAVBAR_SETTINGS.navbar_inner_border_color,

          footer_home_background_color:
            settings.footer_home_background_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_home_background_color,

          footer_home_text_color:
            settings.footer_home_text_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_home_text_color,

          footer_home_link_color:
            settings.footer_home_link_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_home_link_color,

          footer_home_border_color:
            settings.footer_home_border_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_home_border_color,

          footer_inner_background_color:
            settings.footer_inner_background_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_inner_background_color,

          footer_inner_text_color:
            settings.footer_inner_text_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_inner_text_color,

          footer_inner_link_color:
            settings.footer_inner_link_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_inner_link_color,

          footer_inner_border_color:
            settings.footer_inner_border_color ||
            DEFAULT_NAVBAR_SETTINGS.footer_inner_border_color,
          footer_padding_y:
            Number(settings.footer_padding_y) ||
            DEFAULT_NAVBAR_SETTINGS.footer_padding_y,
        });
      } catch (error) {
        console.error("Navbar settings fetch failed", error);
        alert(
          error?.response?.data?.message ||
          "Could not load navbar settings."
        );
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    loadNavbarSettings();

    return () => {
      alive = false;
    };
  }, [id]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setSuccessMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isSuperAdmin) {
      alert("Only super admins can update navbar settings.");
      return;
    }

    const normalizedHeight = Number(formData.navbar_height);

    if (
      !Number.isFinite(normalizedHeight) ||
      normalizedHeight < 50 ||
      normalizedHeight > 250
    ) {
      alert("Navbar height must be between 50 and 250 pixels.");
      return;
    }
    const normalizedLogoHeight = Number(formData.navbar_logo_height);

    if (
      !Number.isFinite(normalizedLogoHeight) ||
      normalizedLogoHeight < 30 ||
      normalizedLogoHeight > 200
    ) {
      alert("Navbar logo height must be between 30 and 200 pixels.");
      return;
    }
    try {
      setSaving(true);
      setSuccessMessage("");

      await api.put(`/api/organizations/${id}/navbar-settings`, {
        navbar_height: normalizedHeight,
        navbar_logo_height: normalizedLogoHeight,
        navbar_home_mode: formData.navbar_home_mode,
        navbar_home_link_color:
          formData.navbar_home_link_color,
        navbar_inner_background_color:
          formData.navbar_inner_background_color,
        navbar_inner_link_color:
          formData.navbar_inner_link_color,
        navbar_home_background_color:
          formData.navbar_home_background_color,

        navbar_home_border_color:
          formData.navbar_home_border_color,

        navbar_inner_border_color:
          formData.navbar_inner_border_color,

        footer_home_background_color:
          formData.footer_home_background_color,

        footer_home_text_color:
          formData.footer_home_text_color,

        footer_home_link_color:
          formData.footer_home_link_color,

        footer_home_border_color:
          formData.footer_home_border_color,

        footer_inner_background_color:
          formData.footer_inner_background_color,

        footer_inner_text_color:
          formData.footer_inner_text_color,

        footer_inner_link_color:
          formData.footer_inner_link_color,

        footer_inner_border_color:
          formData.footer_inner_border_color,
        footer_padding_y:
          Number(formData.footer_padding_y),
      });

      setSuccessMessage("Navbar settings saved successfully.");
    } catch (error) {
      console.error("Navbar settings save failed", error);

      alert(
        error?.response?.data?.message ||
        "Could not save navbar settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(DEFAULT_NAVBAR_SETTINGS);
    setSuccessMessage("");
  };

  if (loading) return null;

  if (!isSuperAdmin) {
    return (
      <div className="navbarSettingsPage">
        <div className="navbarSettingsPage-card">
          <h1 className="navbarSettingsPage-title">
            Navbar Settings
          </h1>

          <p className="navbarSettingsPage-subtext">
            You do not have permission to manage these settings.
          </p>

          <button
            type="button"
            className="navbarSettingsPage-button navbarSettingsPage-button--secondary"
            onClick={() =>
              navigate(`/dashboard/organizations/${id}`)
            }
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const previewIsHomepage = previewMode === "homepage";
  const previewLogoUrl =
    organization?.logo_url ||
    organization?.settings?.logo_url ||
    "";
  const previewIsOverlay =
    previewIsHomepage &&
    formData.navbar_home_mode === "overlay";



    
  return (
    <div className="navbarSettingsPage">
      <div className="navbarSettingsPage-card">
        <div className="navbarSettingsPage-header">
          <div>
            <h1 className="navbarSettingsPage-title">
              Navbar Settings
            </h1>

            <p className="navbarSettingsPage-subtext">
              Customize the public navbar for{" "}
              {organization?.name || "this organization"}.
            </p>
          </div>

          <button
            type="button"
            className="navbarSettingsPage-button navbarSettingsPage-button--secondary"
            onClick={() =>
              navigate(`/dashboard/organizations/${id}`)
            }
          >
            Back
          </button>
        </div>
        <div className="navbarSettingsPage-previewToggle">
          <button
            type="button"
            className={settingsTab === "navbar" ? "is-active" : ""}
            onClick={() => setSettingsTab("navbar")}
          >
            Navbar
          </button>

          <button
            type="button"
            className={settingsTab === "footer" ? "is-active" : ""}
            onClick={() => setSettingsTab("footer")}
          >
            Footer
          </button>
        </div>
        {successMessage && (
          <div className="navbarSettingsPage-success">
            {successMessage}
          </div>
        )}

        <form
          className="navbarSettingsPage-form"
          onSubmit={handleSubmit}
        >
          {settingsTab === "navbar" && (
          <>
          <div className="navbarSettingsPage-section">
            <h2 className="navbarSettingsPage-sectionTitle">
              Navbar Layout
            </h2>

            <div className="navbarSettingsPage-field">
              <label htmlFor="navbar-height">
                Navbar Height
              </label>

              <div className="navbarSettingsPage-numberWrap">
                <input
                  id="navbar-height"
                  type="number"
                  min="50"
                  max="250"
                  value={formData.navbar_height}
                  onChange={(event) =>
                    handleChange(
                      "navbar_height",
                      event.target.value
                    )
                  }
                />

                <span>px</span>
              </div>

              <p>
                Controls the navbar height on all public pages.
              </p>
            </div>
            <div className="navbarSettingsPage-field">
              <label htmlFor="navbar-logo-height">
                Logo Height
              </label>

              <div className="navbarSettingsPage-numberWrap">
                <input
                  id="navbar-logo-height"
                  type="number"
                  min="30"
                  max="200"
                  value={formData.navbar_logo_height}
                  onChange={(event) =>
                    handleChange(
                      "navbar_logo_height",
                      event.target.value
                    )
                  }
                />

                <span>px</span>
              </div>

              <p>Controls the logo size inside the navbar.</p>
            </div>
            <div className="navbarSettingsPage-field">
              <label htmlFor="navbar-home-mode">
                Homepage Navbar Style
              </label>

              <select
                id="navbar-home-mode"
                value={formData.navbar_home_mode}
                onChange={(event) =>
                  handleChange(
                    "navbar_home_mode",
                    event.target.value
                  )
                }
              >
                <option value="solid">Solid</option>
                <option value="overlay">
                  Transparent Overlay
                </option>
              </select>

              <p>
                Overlay places the navbar over the homepage hero.
                Inner pages remain solid.
              </p>
            </div>
          </div>

          <div className="navbarSettingsPage-section">
            <h2 className="navbarSettingsPage-sectionTitle">
              Homepage Colors
            </h2>
                <div className="navbarSettingsPage-field">
                  <label htmlFor="navbar-home-background-color">
                    Homepage Background Color
                  </label>

                  <div className="navbarSettingsPage-colorRow">
                    <input
                      id="navbar-home-background-color"
                      type="color"
                      value={formData.navbar_home_background_color}
                      onChange={(event) =>
                        handleChange(
                          "navbar_home_background_color",
                          event.target.value
                        )
                      }
                    />

                    <input
                      type="text"
                      value={formData.navbar_home_background_color}
                      onChange={(event) =>
                        handleChange(
                          "navbar_home_background_color",
                          event.target.value
                        )
                      }
                    />
                  </div>
                </div>

                <div className="navbarSettingsPage-field">
                  <label htmlFor="navbar-home-border-color">
                    Homepage Bottom Border Color
                  </label>

                  <div className="navbarSettingsPage-colorRow">
                    <input
                      id="navbar-home-border-color"
                      type="color"
                      value={formData.navbar_home_border_color}
                      onChange={(event) =>
                        handleChange(
                          "navbar_home_border_color",
                          event.target.value
                        )
                      }
                    />

                    <input
                      type="text"
                      value={formData.navbar_home_border_color}
                      onChange={(event) =>
                        handleChange(
                          "navbar_home_border_color",
                          event.target.value
                        )
                      }
                    />
                  </div>
                </div>
            <div className="navbarSettingsPage-field">
              <label htmlFor="navbar-home-link-color">
                Homepage Link Color
              </label>

              <div className="navbarSettingsPage-colorRow">
                <input
                  id="navbar-home-link-color"
                  type="color"
                  value={formData.navbar_home_link_color}
                  onChange={(event) =>
                    handleChange(
                      "navbar_home_link_color",
                      event.target.value
                    )
                  }
                />

                <input
                  type="text"
                  value={formData.navbar_home_link_color}
                  onChange={(event) =>
                    handleChange(
                      "navbar_home_link_color",
                      event.target.value
                    )
                  }
                />
              </div>
            </div>
          </div>

          <div className="navbarSettingsPage-section">
            <h2 className="navbarSettingsPage-sectionTitle">
              Inner Page Colors
            </h2>

            <div className="navbarSettingsPage-fieldsGrid">
                  <div className="navbarSettingsPage-field">
                    <label htmlFor="navbar-inner-border-color">
                      Bottom Border Color
                    </label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        id="navbar-inner-border-color"
                        type="color"
                        value={formData.navbar_inner_border_color}
                        onChange={(event) =>
                          handleChange(
                            "navbar_inner_border_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.navbar_inner_border_color}
                        onChange={(event) =>
                          handleChange(
                            "navbar_inner_border_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>
              <div className="navbarSettingsPage-field">
                <label htmlFor="navbar-inner-background">
                  Background Color
                </label>

                <div className="navbarSettingsPage-colorRow">
                  <input
                    id="navbar-inner-background"
                    type="color"
                    value={
                      formData.navbar_inner_background_color
                    }
                    onChange={(event) =>
                      handleChange(
                        "navbar_inner_background_color",
                        event.target.value
                      )
                    }
                  />

                  <input
                    type="text"
                    value={
                      formData.navbar_inner_background_color
                    }
                    onChange={(event) =>
                      handleChange(
                        "navbar_inner_background_color",
                        event.target.value
                      )
                    }
                  />
                </div>
              </div>

              <div className="navbarSettingsPage-field">
                <label htmlFor="navbar-inner-link-color">
                  Link Color
                </label>

                <div className="navbarSettingsPage-colorRow">
                  <input
                    id="navbar-inner-link-color"
                    type="color"
                    value={formData.navbar_inner_link_color}
                    onChange={(event) =>
                      handleChange(
                        "navbar_inner_link_color",
                        event.target.value
                      )
                    }
                  />

                  <input
                    type="text"
                    value={formData.navbar_inner_link_color}
                    onChange={(event) =>
                      handleChange(
                        "navbar_inner_link_color",
                        event.target.value
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="navbarSettingsPage-previewToggle">
            <button
              type="button"
              className={previewMode === "homepage" ? "is-active" : ""}
              onClick={() => setPreviewMode("homepage")}
            >
              Homepage
            </button>

            <button
              type="button"
              className={previewMode === "inner" ? "is-active" : ""}
              onClick={() => setPreviewMode("inner")}
            >
              Inner Page
            </button>
          </div>
          <div className="navbarSettingsPage-section">
            <h2 className="navbarSettingsPage-sectionTitle">
              Preview
            </h2>

            <div
              className={`navbarSettingsPage-preview ${previewIsOverlay
                ? "navbarSettingsPage-preview--overlay"
                : "navbarSettingsPage-preview--solid"
                }`}
            >
              <div
                className="navbarSettingsPage-previewHero"
                style={{
                  background: previewIsOverlay
                    ? "linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.08))"
                    : formData.navbar_inner_background_color,
                }}
              >
                <div
                  className="navbarSettingsPage-previewNavbar"
                  style={{
                    height: `${formData.navbar_height}px`,
                    backgroundColor: previewIsOverlay
                      ? "transparent"
                      : previewIsHomepage
                        ? formData.navbar_home_background_color
                        : formData.navbar_inner_background_color,
                    color: previewIsHomepage
                      ? formData.navbar_home_link_color
                      : formData.navbar_inner_link_color,
                    borderBottom: `1px solid ${previewIsHomepage
                        ? formData.navbar_home_border_color
                        : formData.navbar_inner_border_color
                      }`,
                  }}
                >
                  <div className="navbarSettingsPage-previewLogo">
                    {previewLogoUrl ? (
                      <img
                        src={previewLogoUrl}
                        alt={organization?.name || "Organization logo"}
                        style={{
                          height: `${formData.navbar_logo_height}px`,
                          width: "auto",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      organization?.name || "Organization"
                    )}
                  </div>

                  <div className="navbarSettingsPage-previewLinks">
                    {navPages.map((page) => (
                      <span key={page.id}>
                        {page.name}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="navbarSettingsPage-previewHeroText">
                  Homepage Hero
                </div>
              </div>

            </div>
          </div>
   </>
  )}

          {settingsTab === "footer" && (
            <>
              <div className="navbarSettingsPage-section">
                <h2 className="navbarSettingsPage-sectionTitle">
                  Homepage Footer Colors
                </h2>

                <div className="navbarSettingsPage-fieldsGrid">
                  <div className="navbarSettingsPage-field">
                    <label>Background Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_home_background_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_background_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_home_background_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_background_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="navbarSettingsPage-field">
                    <label>Text Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_home_text_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_text_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_home_text_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_text_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="navbarSettingsPage-field">
                    <label>Link Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_home_link_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_link_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_home_link_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_link_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="navbarSettingsPage-field">
                    <label>Top Border Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_home_border_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_border_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_home_border_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_home_border_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="navbarSettingsPage-section">
                <h2 className="navbarSettingsPage-sectionTitle">
                  Inner Page Footer Colors
                </h2>

                <div className="navbarSettingsPage-fieldsGrid">
                  <div className="navbarSettingsPage-field">
                    <label>Background Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_inner_background_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_background_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_inner_background_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_background_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="navbarSettingsPage-field">
                    <label>Text Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_inner_text_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_text_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_inner_text_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_text_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="navbarSettingsPage-field">
                    <label>Link Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_inner_link_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_link_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_inner_link_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_link_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>

                  <div className="navbarSettingsPage-field">
                    <label>Top Border Color</label>

                    <div className="navbarSettingsPage-colorRow">
                      <input
                        type="color"
                        value={formData.footer_inner_border_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_border_color",
                            event.target.value
                          )
                        }
                      />

                      <input
                        type="text"
                        value={formData.footer_inner_border_color}
                        onChange={(event) =>
                          handleChange(
                            "footer_inner_border_color",
                            event.target.value
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="navbarSettingsPage-section">
                <div className="navbarSettingsPage-field">
                  <label htmlFor="footer-padding-y">
                    Vertical Padding
                  </label>

                  <div className="navbarSettingsPage-numberWrap">
                    <input
                      id="footer-padding-y"
                      type="number"
                      min="0"
                      max="100"
                      value={formData.footer_padding_y}
                      onChange={(event) =>
                        handleChange(
                          "footer_padding_y",
                          event.target.value
                        )
                      }
                    />

                    <span>px</span>
                  </div>
                </div>
              </div>
            </>
          )}


          <div className="navbarSettingsPage-actions">
          
            <button
              type="button"
              className="navbarSettingsPage-button navbarSettingsPage-button--secondary"
              onClick={handleReset}
              disabled={saving}
            >
              Reset to Defaults
            </button>

            <button
              type="submit"
              className="navbarSettingsPage-button navbarSettingsPage-button--primary"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Navbar Settings"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NavbarSettings;